import {
  type Channel,
  type PricingCalculateResponse,
  type Settings,
  formatBRL,
  formatBps,
  leadTimeDays,
} from '@deizi/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTimeline } from '../../lib/queue-api';
import { Icon } from '../../components/Icon';
import { centavosInput, formatGrams, formatHm, parseBps } from '../../lib/numbers';
import { useIsPhone } from '../../lib/use-media';
import type { Draft } from './draft';

type ChannelResult = PricingCalculateResponse['channels'][number];
type Update = (fn: (d: Draft) => Draft) => void;

const COST_ROWS = [
  { key: 'material', label: 'Material', color: '#FF5C5C' },
  { key: 'machine', label: 'Máquina', color: '#0F5D6B' },
  { key: 'failures', label: 'Reserva de falhas', color: '#9A5B00' },
  { key: 'labor', label: 'Mão de obra', color: '#F4A08F' },
  { key: 'packaging', label: 'Embalagem', color: '#7FB2BA' },
  { key: 'supplies', label: 'Insumos', color: '#C9A227' },
  { key: 'design', label: 'Projeto (por unidade)', color: 'var(--ink)' },
] as const;

const dayLabel = (n: number) => (n === 1 ? '~1 dia' : `~${n} dias`);

export function leadDays(
  result: PricingCalculateResponse,
  settings: Settings | undefined,
  backlogSeconds = 0,
): number {
  return leadTimeDays(
    result.totals.printSeconds,
    settings?.rules.printHoursPerDay ?? 10,
    backlogSeconds,
  );
}

/** Hours already in the print queue (soonest-free printer). */
export function useBacklog(): number {
  return useTimeline().data?.backlogSeconds ?? 0;
}

/** "Custo unitário" card with the stacked bar and the breakdown (boards QQ-Desktop / QQ-Celular-Resultado). */
export function CostCard({
  result,
  settings,
}: {
  result: PricingCalculateResponse;
  settings: Settings | undefined;
}) {
  const phone = useIsPhone();
  const [open, setOpen] = useState(!phone);
  const c = result.cost;
  const wasteBps = settings?.costs.wasteBps ?? 0;
  const failureBps = settings?.costs.failureBps;
  const notes: Partial<Record<(typeof COST_ROWS)[number]['key'], string>> = {
    material: `${formatGrams(result.perUnit.grams)} + ${formatBps(wasteBps)} desperdício`,
    machine: `${formatHm(result.perUnit.printSeconds)} × ${formatBRL(result.machinePerHourCentavos)}/h`,
    ...(failureBps != null ? { failures: formatBps(failureBps) } : {}),
  };
  const rows = COST_ROWS.filter((r) => c[r.key] > 0 || r.key === 'material' || r.key === 'machine');
  const kv = (r: (typeof COST_ROWS)[number]) => (
    <div key={r.key} className="kv">
      <span>
        <i className="sw" style={{ background: r.color, marginRight: 8 }} />
        {r.label} {notes[r.key] ? <span className="xs muted">{notes[r.key]}</span> : null}
      </span>
      <span className="v">{formatBRL(c[r.key])}</span>
    </div>
  );
  const machine = (
    <div key="machine" role="group" aria-label="Máquina e suas partes">
      {kv(COST_ROWS[1])}
      <div style={{ marginLeft: 4, paddingLeft: 16, borderLeft: '2px solid var(--line)' }}>
        {(
          [
            ['Depreciação', c.machineParts.depreciation],
            ['Energia', c.machineParts.energy],
            ['Manutenção', c.machineParts.maintenance],
          ] as const
        ).map(([l, v]) => (
          <div key={l} className="kv sm">
            <span className="muted">{l}</span>
            <span className="v muted">{formatBRL(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
  const backlog = useBacklog();
  const days = leadDays(result, settings, backlog);
  return (
    <section className="card col" style={{ gap: 14 }}>
      <div className="row between wrap" style={{ alignItems: 'flex-end', gap: 12 }}>
        <div>
          <p className="lbl">Custo unitário</p>
          <p className="big">{formatBRL(c.unitCost)}</p>
        </div>
        {phone ? (
          <button
            type="button"
            className="btn sm g"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? 'Ocultar' : 'Composição'}
          </button>
        ) : (
          <div className="row" style={{ gap: 28 }}>
            <div>
              <p className="lbl">Impressão total</p>
              <p className="fr tab" style={{ fontSize: 22 }}>
                {formatHm(result.totals.printSeconds)} ·{' '}
                {result.totals.plates === 1 ? '1 placa' : `${result.totals.plates} placas`}
              </p>
            </div>
            <div>
              <p className="lbl">Pronto em</p>
              <p className="fr tab" style={{ fontSize: 22 }}>
                {dayLabel(days)}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="stack" aria-hidden="true">
        {COST_ROWS.map((r) =>
          c[r.key] > 0 ? (
            <span key={r.key} style={{ flex: c[r.key], background: r.color }} />
          ) : null,
        )}
      </div>
      {phone ? null : (
        <button
          type="button"
          className="btn sm g"
          style={{ alignSelf: 'flex-start', padding: 0 }}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Ocultar composição' : 'Ver composição do custo'}
        </button>
      )}
      {open ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(280px,100%),1fr))',
            columnGap: 28,
            alignItems: 'start',
          }}
        >
          <div className="col" style={{ gap: 0, minWidth: 0 }}>
            {kv(COST_ROWS[0])}
            {machine}
          </div>
          <div className="col" style={{ gap: 0, minWidth: 0 }}>
            {rows.filter((r) => r.key !== 'material' && r.key !== 'machine').map(kv)}
          </div>
        </div>
      ) : (
        <p className="xs muted">
          {rows.map((r) => `${r.label.toLowerCase()} ${formatBRL(c[r.key])}`).join(' · ')}
        </p>
      )}
    </section>
  );
}

interface Row {
  channel: Channel;
  r: ChannelResult;
  tone: 'ok' | 'warn' | 'err';
  note: string | null;
  feesNote: string;
  override: string;
}

function rowsOf(
  result: PricingCalculateResponse,
  channels: Channel[],
  draft: Draft,
  minimumMarginBps: number | null,
): Row[] {
  return channels.flatMap((channel) => {
    const r = result.channels.find((x) => x.channelId === channel.id);
    if (!r) return [];
    const ev = r.evaluation;
    let tone: Row['tone'] = 'ok';
    let note: string | null = null;
    if (!ev) {
      tone = 'err';
      note = 'Margem impossível neste canal';
    } else if (ev.state === 'below_cost') {
      tone = 'err';
      note = `Abaixo do custo: prejuízo de ${formatBRL(-ev.netProfitCentavos)}`;
    } else if (ev.state === 'below_minimum_margin') {
      tone = 'warn';
      note = `Abaixo da margem mínima${minimumMarginBps != null ? ` (${formatBps(minimumMarginBps)})` : ''}`;
    }
    const min = r.warnings.find((w) => w.code === 'below_minimum_order');
    if (!note && min && min.code === 'below_minimum_order') {
      tone = 'warn';
      note = `Pedido abaixo do mínimo de ${formatBRL(min.minimumOrderCentavos)}`;
    }
    const v = channel.current;
    const parts: string[] = [];
    if (v?.commissionBps) parts.push(formatBps(v.commissionBps));
    if (v?.paymentFeeBps) parts.push(formatBps(v.paymentFeeBps));
    if (ev?.fees.fixedFee) parts.push(formatBRL(ev.fees.fixedFee));
    if (ev?.fees.shipping) parts.push(`frete ${formatBRL(ev.fees.shipping)}`);
    if (ev?.fees.tax) parts.push(`imposto ${formatBRL(ev.fees.tax)}`);
    return [
      {
        channel,
        r,
        tone,
        note,
        feesNote: parts.length ? parts.join(' + ') : 'sem taxas',
        override: draft.overrides[channel.id] ?? '',
      },
    ];
  });
}

const toneColor = (tone: Row['tone']) =>
  tone === 'err' ? 'var(--err)' : tone === 'warn' ? 'var(--warn)' : 'var(--ok)';

/** Margin that fits a channel whose fees leave no room for the target ("Usar 25% neste canal"). */
function fittingMargin(r: ChannelResult, shopTarget: number | null): number | null {
  const feesBps = r.percentOfPriceBps - r.targetMarginBps;
  const room = 10_000 - feesBps - 500;
  if (room <= 0) return null;
  const fit = Math.floor(room / 500) * 500;
  return Math.min(shopTarget ?? fit, fit);
}

const minus = (n: number) => (n === 0 ? formatBRL(0) : `− ${formatBRL(n)}`);

function FeeLines({ row, unitCost }: { row: Row; unitCost: number }) {
  const ev = row.r.evaluation;
  if (!ev) return null;
  const v = row.channel.current;
  const range = ev.fees.fixedFeeRange;
  const lines: { l: string; note: string; v: string; strong?: boolean; color?: string }[] = [
    {
      l: 'Preço de venda',
      note:
        ev.source === 'override'
          ? 'seu preço'
          : row.r.suggested
            ? `sugerido ${formatBRL(row.r.suggested.rawCentavos)} → arredondado`
            : '',
      v: formatBRL(ev.priceCentavos),
      strong: true,
    },
    {
      l: `Comissão ${formatBps(v?.commissionBps ?? 0)}`,
      note: v?.commissionBps ? 'sobre o preço' : 'sem comissão',
      v: minus(ev.fees.commission),
    },
    {
      l: 'Tarifa fixa',
      note: range
        ? `de ${formatBRL(range.fromCentavos)}${range.toCentavos != null ? ` a ${formatBRL(range.toCentavos)}` : ' para cima'}`
        : 'não tem',
      v: minus(ev.fees.fixedFee),
    },
    {
      l: 'Frete',
      note: ev.fees.sellerPaysShipping ? 'frete grátis por sua conta' : 'comprador paga',
      v: minus(ev.fees.shipping),
    },
    {
      l: 'Taxa de pagamento',
      note: v?.paymentFeeBps ? formatBps(v.paymentFeeBps) : 'não tem',
      v: minus(ev.fees.paymentFee),
    },
    {
      l: 'Imposto',
      note: ev.fees.tax ? 'sobre a venda' : 'DAS fixo, fora do preço',
      v: minus(ev.fees.tax),
    },
    { l: 'Custo unitário', note: '', v: minus(unitCost) },
    {
      l: 'Lucro líquido',
      note: `margem ${formatBps(ev.marginBps)}`,
      v: formatBRL(ev.netProfitCentavos),
      strong: true,
      color: toneColor(row.tone),
    },
  ];
  return (
    <>
      {lines.map((x) => (
        <div key={x.l} className="kv sm">
          <span>
            <span style={{ fontWeight: x.strong ? 600 : 400 }}>{x.l}</span>{' '}
            <span className="xs muted">{x.note}</span>
          </span>
          <span className="v" style={{ fontWeight: x.strong ? 600 : 400, color: x.color }}>
            {x.v}
          </span>
        </div>
      ))}
    </>
  );
}

function CliffNote({ row, update }: { row: Row; update: Update }) {
  const w = row.r.warnings.find((x) => x.code === 'fee_cliff');
  if (!w || w.code !== 'fee_cliff') return null;
  const ship = row.channel.current?.shippingAverageCentavos ?? 0;
  const text = w.freeShipping
    ? `A partir de ${formatBRL(w.thresholdCentavos)} entra o frete grátis${ship ? ` (≈ ${formatBRL(ship)})` : ''}. A ${formatBRL(w.suggestedPriceCentavos)} o lucro é ${formatBRL(w.netProfitCentavos)} (${formatBps(w.marginBps)}).`
    : `A partir de ${formatBRL(w.thresholdCentavos)} a tarifa muda. A ${formatBRL(w.suggestedPriceCentavos)} o lucro é ${formatBRL(w.netProfitCentavos)} (${formatBps(w.marginBps)}).`;
  return (
    <div
      className="row"
      role="note"
      style={{ gap: 10, padding: '8px 18px 10px 34px', background: 'var(--warn-bg)' }}
    >
      <Icon name="alert" size={18} style={{ color: 'var(--warn)', flex: '0 0 auto' }} />
      <p className="sm grow" style={{ minWidth: 0, color: 'var(--warn)', fontWeight: 500 }}>
        {text}
      </p>
      <button
        type="button"
        className="btn sm"
        style={{ flex: '0 0 auto' }}
        onClick={() =>
          update((d) => ({
            ...d,
            overrides: {
              ...d.overrides,
              [row.channel.id]: centavosInput(w.suggestedPriceCentavos),
            },
          }))
        }
      >
        Usar {formatBRL(w.suggestedPriceCentavos)}
      </button>
    </div>
  );
}

function ImpossibleNote({
  row,
  update,
  shopTarget,
}: {
  row: Row;
  update: Update;
  shopTarget: number | null;
}) {
  const feesBps = row.r.percentOfPriceBps - row.r.targetMarginBps;
  const fit = fittingMargin(row.r, shopTarget);
  return (
    <div style={{ padding: '4px 18px 14px 34px' }}>
      <div className="alert err">
        <Icon name="alert" />
        <div className="col" style={{ gap: 8 }}>
          <p className="sm">
            <b>
              Taxas {formatBps(feesBps)} + margem alvo {formatBps(row.r.targetMarginBps)} ={' '}
              {formatBps(row.r.percentOfPriceBps)} do preço.
            </b>{' '}
            Não sobra nada para pagar o custo, em qualquer preço.
          </p>
          <div className="row wrap" style={{ gap: 8 }}>
            {fit !== null ? (
              <button
                type="button"
                className="btn sm"
                onClick={() =>
                  update((d) => ({
                    ...d,
                    channelMargins: { ...d.channelMargins, [row.channel.id]: fit },
                  }))
                }
              >
                Usar {formatBps(fit)} neste canal
              </button>
            ) : null}
            <button
              type="button"
              className="btn sm g"
              onClick={() => update((d) => ({ ...d, hidden: [...d.hidden, row.channel.id] }))}
            >
              Esconder canal deste orçamento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomMargin({ row, update }: { row: Row; update: Update }) {
  return (
    <div className="row" style={{ gap: 6, marginTop: 4 }}>
      <span className="badge info" style={{ height: 22, fontSize: 12 }}>
        Margem {formatBps(row.r.targetMarginBps)} neste canal
      </span>
      <button
        type="button"
        className="btn sm g"
        style={{ minHeight: 24, padding: '0 4px', fontSize: 13 }}
        onClick={() =>
          update((d) => {
            const { [row.channel.id]: _drop, ...rest } = d.channelMargins;
            return { ...d, channelMargins: rest };
          })
        }
      >
        Desfazer
      </button>
    </div>
  );
}

function OverrideInput({ row, update, id }: { row: Row; update: Update; id?: string }) {
  const ev = row.r.evaluation;
  const has = row.override.trim() !== '';
  return (
    <input
      id={id}
      className="ovr"
      inputMode="decimal"
      aria-label={id ? undefined : `Seu preço para ${row.channel.name}`}
      placeholder={row.r.suggested ? centavosInput(row.r.suggested.priceCentavos) : ''}
      value={row.override}
      style={{
        borderColor:
          has && ev ? (row.tone === 'ok' ? 'var(--pe)' : toneColor(row.tone)) : undefined,
      }}
      onChange={(e) =>
        update((d) => ({ ...d, overrides: { ...d.overrides, [row.channel.id]: e.target.value } }))
      }
    />
  );
}

function clearOverride(update: Update, id: string) {
  update((d) => {
    const { [id]: _drop, ...rest } = d.overrides;
    return { ...d, overrides: rest };
  });
}

function ChannelsMenu({
  channels,
  draft,
  update,
}: {
  channels: Channel[];
  draft: Draft;
  update: Update;
}) {
  const [open, setOpen] = useState(false);
  const visible = channels.filter((c) => !draft.hidden.includes(c.id)).length;
  return (
    <>
      <button
        type="button"
        className="btn sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon name="tag" size={16} />
        Canais · {visible} de {channels.length}
      </button>
      {open ? (
        <div
          className="menu"
          role="menu"
          aria-label="Canais visíveis"
          style={{ position: 'absolute', right: 18, top: 58, zIndex: 20, width: 260 }}
        >
          <p className="xs muted" style={{ padding: '6px 12px 4px' }}>
            Mostrar neste orçamento
          </p>
          {channels.map((c) => {
            const on = !draft.hidden.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={on}
                onClick={() =>
                  update((d) => ({
                    ...d,
                    hidden: on ? [...d.hidden, c.id] : d.hidden.filter((x) => x !== c.id),
                  }))
                }
              >
                <span className={on ? 'box on' : 'box'}>
                  {on ? <Icon name="check" size={14} strokeWidth={3} /> : null}
                </span>
                <span className="dot" style={{ background: c.color }} />
                {c.name}
              </button>
            );
          })}
          <div className="hr" style={{ margin: '4px 0' }} />
          <div className="row between" style={{ padding: '2px 4px' }}>
            <button
              type="button"
              className="btn sm g"
              onClick={() => update((d) => ({ ...d, hidden: [] }))}
            >
              Mostrar todos
            </button>
            <button type="button" className="btn sm" onClick={() => setOpen(false)}>
              Pronto
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function footNote(settings: Settings | undefined, hidden: number): string {
  const tax =
    settings?.costs.taxMode === 'percent'
      ? `Imposto: ${formatBps(settings.costs.taxBps ?? 0)} sobre a venda.`
      : 'Imposto: MEI com DAS fixo, contado no ponto de equilíbrio.';
  const h = hidden
    ? ` ${hidden} ${hidden > 1 ? 'canais ocultos' : 'canal oculto'} neste orçamento.`
    : '';
  return `${tax} Taxas em vigor hoje.${h}`;
}

const COLS = 'minmax(0,1.5fr) minmax(0,1fr) 100px minmax(0,1.05fr) minmax(0,1fr) 58px 32px';

/** Desktop "Preço por canal" table. */
export function ChannelTable({
  result,
  channels,
  draft,
  update,
  settings,
}: {
  result: PricingCalculateResponse;
  channels: Channel[];
  draft: Draft;
  update: Update;
  settings: Settings | undefined;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const shown = channels.filter((c) => !draft.hidden.includes(c.id));
  const rows = rowsOf(result, shown, draft, settings?.costs.minimumMarginBps ?? null);
  const rounding = settings?.rules.rounding;
  return (
    <section className="card flat" style={{ overflow: 'visible' }}>
      <div
        className="row between wrap"
        style={{ padding: '16px 18px 6px', gap: 10, position: 'relative' }}
      >
        <div>
          <h2>Preço por canal</h2>
          <p className="xs muted">
            {rounding === 'end_90'
              ? 'Arredonda para ,90 · '
              : rounding === 'end_99'
                ? 'Arredonda para ,99 · '
                : rounding === 'whole'
                  ? 'Arredonda para o real · '
                  : ''}
            digite seu preço para simular · clique no canal para ver as taxas
          </p>
        </div>
        <ChannelsMenu channels={channels} draft={draft} update={update} />
      </div>
      <div className="gt" role="table" aria-label="Preço por canal">
        <div className="gtr head" role="row" style={{ gridTemplateColumns: COLS }}>
          <span>Canal</span>
          <span className="num">Sugerido</span>
          <span className="num">Seu preço</span>
          <span className="num">Taxas</span>
          <span className="num">Lucro líquido</span>
          <span className="num">Margem</span>
          <span />
        </div>
        {rows.length === 0 ? (
          <div className="sm muted" style={{ padding: 16 }}>
            Nenhum canal visível.{' '}
            <button
              type="button"
              className="btn sm g"
              onClick={() => update((d) => ({ ...d, hidden: [] }))}
            >
              Mostrar todos
            </button>
          </div>
        ) : null}
        {rows.map((row) => {
          const ev = row.r.evaluation;
          const isOpen = expanded === row.channel.id;
          const toggle = () => setExpanded(isOpen ? null : row.channel.id);
          const cls = row.tone === 'err' ? 'gtr hl' : row.tone === 'warn' ? 'gtr wl' : 'gtr';
          return (
            <div
              key={row.channel.id}
              style={{ borderBottom: '1px solid var(--line)' }}
              role="rowgroup"
            >
              <div
                className={cls}
                role="row"
                style={{ gridTemplateColumns: COLS, borderBottom: 0 }}
              >
                <div style={{ minWidth: 0 }}>
                  <button type="button" className="chn" onClick={toggle} aria-expanded={isOpen}>
                    <span className="dot" style={{ background: row.channel.color }} />
                    {row.channel.name}
                  </button>
                  {row.note ? (
                    <div
                      className="xs"
                      style={{
                        color: toneColor(row.tone),
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                        marginTop: 2,
                        fontWeight: 500,
                      }}
                    >
                      <Icon name="alert" size={14} />
                      {row.note}
                    </div>
                  ) : null}
                  {draft.channelMargins[row.channel.id] !== undefined ? (
                    <CustomMargin row={row} update={update} />
                  ) : null}
                </div>
                <div className="num">
                  {row.r.suggested ? (
                    <>
                      <span className="muted xs">{formatBRL(row.r.suggested.rawCentavos)}</span>
                      <br />
                      <b style={{ fontWeight: 600 }}>{formatBRL(row.r.suggested.priceCentavos)}</b>
                    </>
                  ) : (
                    '—'
                  )}
                </div>
                <div className="num">
                  <OverrideInput row={row} update={update} />
                </div>
                <div className="num">
                  {ev
                    ? `− ${formatBRL(ev.fees.total)}`
                    : formatBps(row.r.percentOfPriceBps - row.r.targetMarginBps)}
                  <br />
                  <span className="xs muted">{row.feesNote}</span>
                </div>
                <div className="num" style={{ fontWeight: 600, color: toneColor(row.tone) }}>
                  {ev ? formatBRL(ev.netProfitCentavos) : '—'}
                </div>
                <div className="num">{ev ? formatBps(ev.marginBps) : '—'}</div>
                <button
                  type="button"
                  className="btn icon sm g"
                  style={{ width: 32, minHeight: 32 }}
                  aria-label={`${isOpen ? 'Recolher taxas de' : 'Ver taxas de'} ${row.channel.name}`}
                  aria-expanded={isOpen}
                  onClick={toggle}
                >
                  <Icon
                    name="chevronRight"
                    size={16}
                    style={{ transform: isOpen ? 'rotate(90deg)' : undefined }}
                  />
                </button>
              </div>
              <CliffNote row={row} update={update} />
              {!ev ? (
                <ImpossibleNote
                  row={row}
                  update={update}
                  shopTarget={settings?.costs.targetMarginBps ?? null}
                />
              ) : null}
              {isOpen && ev ? (
                <div style={{ padding: '6px 18px 16px 34px', background: 'var(--sunk)' }}>
                  <FeeLines row={row} unitCost={result.cost.unitCost} />
                  {row.override ? (
                    <button
                      type="button"
                      className="btn sm g"
                      style={{ padding: 0, marginTop: 6 }}
                      onClick={() => clearOverride(update, row.channel.id)}
                    >
                      Voltar ao sugerido
                      {row.r.suggested ? ` (${formatBRL(row.r.suggested.priceCentavos)})` : ''}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="xs muted" style={{ padding: '10px 18px 16px' }}>
        {footNote(settings, channels.length - shown.length)} Confira em{' '}
        <Link to="/configuracoes/canais">Canais</Link>.
      </p>
    </section>
  );
}

/** Phone accordion of channel cards (board QQ-Celular-Resultado). */
export function ChannelCards({
  result,
  channels,
  draft,
  update,
  settings,
}: {
  result: PricingCalculateResponse;
  channels: Channel[];
  draft: Draft;
  update: Update;
  settings: Settings | undefined;
}) {
  const shown = channels.filter((c) => !draft.hidden.includes(c.id));
  const rows = rowsOf(result, shown, draft, settings?.costs.minimumMarginBps ?? null);
  const [open, setOpen] = useState<string | null>(rows[0]?.channel.id ?? null);
  const target = settings?.costs.targetMarginBps;
  return (
    <>
      <div className="row between">
        <h2>Preço por canal</h2>
        {target != null ? (
          <span className="xs muted">
            margem alvo {formatBps((draft.marginPct ? parseBps(draft.marginPct) : null) ?? target)}
          </span>
        ) : null}
      </div>
      {rows.map((row) => {
        const ev = row.r.evaluation;
        const isOpen = open === row.channel.id;
        const ovId = `ov-${row.channel.id}`;
        return (
          <article
            key={row.channel.id}
            className="card col"
            style={{
              gap: 10,
              padding: isOpen ? 16 : '12px 16px',
              borderColor: row.tone === 'ok' ? undefined : toneColor(row.tone),
            }}
          >
            <button
              type="button"
              className="acc"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : row.channel.id)}
            >
              <span className="col" style={{ gap: 2, minWidth: 0 }}>
                <span className="row" style={{ gap: 8 }}>
                  <span className="dot" style={{ background: row.channel.color }} />
                  <span className="fr" style={{ fontSize: 16, color: 'var(--head)' }}>
                    {row.channel.name}
                  </span>
                </span>
                {isOpen ? null : (
                  <span
                    className="xs"
                    style={{ color: row.note ? toneColor(row.tone) : 'var(--ink2)' }}
                  >
                    {row.note ??
                      (ev
                        ? `lucro ${formatBRL(ev.netProfitCentavos)} · margem ${formatBps(ev.marginBps)}`
                        : '')}
                  </span>
                )}
              </span>
              {isOpen ? (
                ev ? (
                  <span
                    className={
                      row.tone === 'ok'
                        ? 'badge ok'
                        : row.tone === 'warn'
                          ? 'badge warn'
                          : 'badge err'
                    }
                  >
                    {formatBps(ev.marginBps)}
                  </span>
                ) : null
              ) : (
                <span className="fr tab" style={{ fontSize: 22 }}>
                  {ev ? formatBRL(ev.priceCentavos) : '—'}
                </span>
              )}
            </button>
            <CliffNote row={row} update={update} />
            {!ev ? (
              <ImpossibleNote
                row={row}
                update={update}
                shopTarget={settings?.costs.targetMarginBps ?? null}
              />
            ) : null}
            {draft.channelMargins[row.channel.id] !== undefined ? (
              <CustomMargin row={row} update={update} />
            ) : null}
            {isOpen && ev ? (
              <div className="col" style={{ gap: 10 }}>
                <div className="row between" style={{ alignItems: 'flex-end' }}>
                  <span className="big" style={{ fontSize: 32 }}>
                    {formatBRL(ev.priceCentavos)}
                  </span>
                  <div style={{ textAlign: 'right' }}>
                    <p className="xs muted">lucro líquido</p>
                    <p className="fr tab" style={{ fontSize: 18, color: toneColor(row.tone) }}>
                      {formatBRL(ev.netProfitCentavos)}
                    </p>
                  </div>
                </div>
                <div className="row between" style={{ gap: 10 }}>
                  <label className="sm" htmlFor={ovId}>
                    Seu preço <span className="xs muted">vazio = sugerido</span>
                  </label>
                  <OverrideInput row={row} update={update} id={ovId} />
                </div>
                {row.override ? (
                  <button
                    type="button"
                    className="btn sm g"
                    style={{ alignSelf: 'flex-start', padding: 0 }}
                    onClick={() => clearOverride(update, row.channel.id)}
                  >
                    Voltar ao sugerido
                    {row.r.suggested ? ` (${formatBRL(row.r.suggested.priceCentavos)})` : ''}
                  </button>
                ) : null}
                <div className="col sm" style={{ gap: 0 }}>
                  <FeeLines row={row} unitCost={result.cost.unitCost} />
                </div>
              </div>
            ) : null}
          </article>
        );
      })}
      {channels.length > shown.length ? (
        <p className="xs muted">
          {footNote(settings, channels.length - shown.length)}{' '}
          <button
            type="button"
            className="btn sm g"
            style={{ padding: 0 }}
            onClick={() => update((d) => ({ ...d, hidden: [] }))}
          >
            Mostrar todos
          </button>
        </p>
      ) : null}
    </>
  );
}

/** "Pronto em ~N dias" note with the "?" explanation. */
export function LeadNote({
  result,
  settings,
}: {
  result: PricingCalculateResponse;
  settings: Settings | undefined;
}) {
  const [help, setHelp] = useState(false);
  const backlog = useBacklog();
  const days = leadDays(result, settings, backlog);
  const perDay = settings?.rules.printHoursPerDay ?? 10;
  return (
    <div className="alert">
      <Icon name="queue" />
      <div className="col grow" style={{ gap: 6 }}>
        <div className="row" style={{ gap: 8 }}>
          <p className="sm">
            <b>Pronto em {dayLabel(days)}.</b> {formatHm(result.totals.printSeconds)} de impressão
            desta encomenda.
          </p>
          <button
            type="button"
            className="helpbtn"
            aria-label="Como calculamos o prazo"
            aria-expanded={help}
            onClick={() => setHelp(!help)}
          >
            ?
          </button>
        </div>
        {help ? (
          <p
            className="xs"
            style={{
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              padding: '8px 10px',
            }}
          >
            Somamos o que já está na fila da impressora ({formatHm(backlog)}) às horas desta
            encomenda e contamos ~{perDay} h de impressão por dia, arredondando para cima. Mude a
            jornada em <Link to="/configuracoes/custos">Configurações</Link>.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Lowest–highest visible price, for the phone sticky bar ("R$ 36,90 – R$ 54,90"). */
export function priceRange(
  result: PricingCalculateResponse,
  channels: Channel[],
  draft: Draft,
): string {
  const prices = result.channels
    .filter(
      (c) => channels.some((x) => x.id === c.channelId) && !draft.hidden.includes(c.channelId),
    )
    .flatMap((c) => (c.evaluation ? [c.evaluation.priceCentavos] : []));
  if (prices.length === 0) return '—';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatBRL(min) : `${formatBRL(min)} – ${formatBRL(max)}`;
}
